/**
 * LobbySeat — one seat row in the lobby. Owner: Phase-3 A3.3.
 *
 * Renders:
 *   - seat index + display name
 *   - "bot" badge when `seat.kind === 'bot'`, OR "bot playing for {name}" when
 *     a human's name still occupies a now-bot seat (PRD FR-B3 "Bot is playing
 *     for X" badge).
 *   - "(you)" indicator + "(host)" indicator
 *   - connected dot (offline = dim)
 *   - optional swap control (currently disabled in v1 — see Lobby.tsx for the
 *     decision; the prop is here for forward-compatibility).
 *
 * Detection of "bot covering a human" relies on the fact that the server seeds
 * `displayName` to `'Seat N'` (1-indexed) for a pristine seat, and replaces it
 * with the player's chosen name on `join`. When that named seat later flips to
 * `kind === 'bot'` (disconnect), the name persists — that's the signal a real
 * person was here and is now being covered. See `apps/server/src/room.ts`
 * `freshSeats` + `onClose`.
 */
import type { LobbySeat as LobbySeatData } from "@hookum/protocol";

/** True iff the seat is bot-covering a previously-joined human (PRD FR-B3). */
export function isBotCovering(seat: LobbySeatData): boolean {
  if (seat.kind !== "bot") return false;
  const pristine = `Seat ${seat.seatIndex + 1}`;
  return seat.displayName !== pristine;
}

export interface LobbySeatProps {
  seat: LobbySeatData;
  isYou: boolean;
  isHost: boolean;
  canSwap: boolean;
  onSwap?: () => void;
}

export function LobbySeat({
  seat,
  isYou,
  isHost,
  canSwap,
  onSwap,
}: LobbySeatProps): JSX.Element {
  const covering = isBotCovering(seat);
  const isBot = seat.kind === "bot";
  const teamLabel = seat.seatIndex % 2 === 0 ? "A" : "B";

  // What the right-hand badge says.
  let kindBadge: JSX.Element;
  if (covering) {
    kindBadge = (
      <span className="lobby-seat-badge lobby-seat-badge-covering" title={`Bot is playing for ${seat.displayName}`}>
        bot playing for {seat.displayName}
      </span>
    );
  } else if (isBot) {
    kindBadge = <span className="lobby-seat-badge lobby-seat-badge-bot">bot</span>;
  } else {
    kindBadge = <span className="lobby-seat-badge lobby-seat-badge-human">human</span>;
  }

  // Label shown in the main row. When bot-covering, lead with "Bot" rather
  // than the displayName so the takeover state is unmistakable.
  const nameLabel = covering ? "Bot" : seat.displayName;

  return (
    <li
      className={[
        "lobby-seat",
        isYou ? "lobby-seat-you" : "",
        isHost ? "lobby-seat-host" : "",
        seat.connected ? "lobby-seat-online" : "lobby-seat-offline",
        isBot ? "lobby-seat-bot" : "lobby-seat-human-occ",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label={`Seat ${seat.seatIndex + 1}, team ${teamLabel}, ${nameLabel}${
        isYou ? ", you" : ""
      }${isHost ? ", host" : ""}${seat.connected ? "" : ", offline"}`}
    >
      <span className="lobby-seat-index">#{seat.seatIndex + 1}</span>
      <span className="lobby-seat-team" data-team={teamLabel}>Team {teamLabel}</span>
      <span className="lobby-seat-name">
        <span className={`lobby-seat-dot lobby-seat-dot-${seat.connected ? "on" : "off"}`} aria-hidden="true" />
        {nameLabel}
        {isYou ? <span className="lobby-seat-meta"> (you)</span> : null}
        {isHost ? <span className="lobby-seat-meta"> (host)</span> : null}
      </span>
      {kindBadge}
      {canSwap && onSwap ? (
        <button
          type="button"
          className="lobby-seat-swap"
          onClick={onSwap}
          aria-label={`Swap into seat ${seat.seatIndex + 1}`}
        >
          Swap
        </button>
      ) : null}
    </li>
  );
}
